import { InjectionToken } from '@angular/core'

/**
 * Optional per-tenant opt-in. When true, `EntityDetailComponent` prepends a
 * breadcrumb link to the current type's list view, so root entities without
 * ancestors still offer a one-click path back to the list. Tenants that
 * don't provide this token keep the original breadcrumb shape.
 */
export const ENTITY_DETAIL_SHOW_LIST_BREADCRUMB = new InjectionToken<boolean>(
  'ENTITY_DETAIL_SHOW_LIST_BREADCRUMB'
)

/**
 * Optional per-tenant opt-in. The document viewer renders one row per key of
 * the extraction blob, so its labels are whatever language the ingestion
 * processor's JSON template happens to name its keys in. On a tenant whose
 * sites do not all speak that language, the labels come out foreign: Coty
 * Chartres reads Spanish labels over French content because both sites share
 * the Granollers processor.
 *
 * Provide this token with a translation-key prefix and `EntityDetailComponent`
 * resolves `<prefix>.<normalized_key>` through `TranslateService`, so the
 * labels follow the UI locale instead of the extraction. A key with no
 * translation, and a tenant that does not provide the token at all, fall back
 * to the humanized key.
 */
export const DOCUMENT_DETAIL_LABEL_I18N_PREFIX = new InjectionToken<string>(
  'DOCUMENT_DETAIL_LABEL_I18N_PREFIX'
)

/**
 * Normalize an extraction key to the `snake_case` shape used inside the label
 * bundle: `DocumentTypeDetails` and `document_type_details` both resolve to
 * `document_type_details`, so a processor renaming its keys' casing does not
 * silently drop every label.
 *
 * Exported for the spec and for tenants that generate their bundle from the
 * observed extraction keys.
 */
export function normalizeDetailLabelKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()
    .trim()
}
